import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { DELIVERY_LABEL, deliveryModesOf, formatCuit, MARKET_SORTS, marketFiltersToQuery, parseMarketFilters, SHOP_ACCENTS, type MarketFilters, type ReviewView, type SellerPublic, type ShopAnnouncement, type ShopBannerTarget, type ShopSection } from "../../shared/market";
import { ZONE_LABEL } from "../../shared/shipping";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { Pagination } from "../components/Pagination";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { AsBuyer, Avatar, Badges, ListingGrid, ListingRows, PaymentChips, ReportButton, scheduleText, Stars, useArs, useListingView, ViewToggle } from "./components";
import { MarketFiltersForm } from "./MarketFiltersForm";
import { useCouponOff } from "./CouponsTab";
import { useGame } from "../lib/GameProvider";
import { useCatalog } from "../lib/CatalogProvider";
import { gameFinishes } from "../../shared/games";
import { rarityLabel } from "../../shared/rarity";

/** The sorts that mean something inside one shop ("best seller" doesn't). */
const SHOP_SORTS = MARKET_SORTS.filter((x) => x !== "rating");

/** A shop's filters from its address: the card's only, the shop given (a pasted market link can't empty the list behind filters that aren't shown). */
function shopFilters(params: URLSearchParams, handle: string): MarketFilters {
  const f = parseMarketFilters(params);
  return { ...f, seller: handle, product: null, zone: null, payment: null, minRating: null, minSales: null, sort: f.sort === "rating" ? "newest" : f.sort };
}

/** The shop's address for these filters: never the shop itself, which the path already says. */
const shopQuery = (f: MarketFilters) => marketFiltersToQuery({ ...f, seller: null });

const isFiltered = (f: MarketFilters) =>
  !!(f.q || f.categories.length || f.sets.length || f.rarities.length || f.conditions.length || f.languages.length || f.finish || f.graded !== null || f.minPrice !== null || f.maxPrice !== null);

/** "Ver todas": the filter that shows a section's whole list here, for the templates that have one (not "newest": there's no "the last 14 days"). */
function sectionPatch(x: ShopSection): Partial<MarketFilters> | null {
  return x.type === "under" ? { maxPrice: x.maxPriceArs ?? null } : x.type === "graded" ? { graded: true } : x.type === "set" ? { sets: [x.setName ?? ""] } : null;
}

/** The page's filters: in its address on the shop's page; in the editor's preview, only in the preview (the editor's address stays). */
function useShopFilters(handle: string, preview: boolean): [MarketFilters, (next: MarketFilters) => void] {
  const [params, setParams] = useSearchParams();
  const fromUrl = useMemo(() => shopFilters(params, handle), [params, handle]);
  const [local, setLocal] = useState(() => shopFilters(new URLSearchParams(), handle));
  return preview ? [local, setLocal] : [fromUrl, (next) => setParams(new URLSearchParams(shopQuery(next)))];
}

const dateFmt = (iso: string, locale: "es" | "en", opts: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" }) =>
  new Date(iso).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", opts);

/**
 * A shop's page: banner, name with its reputation and how it sells in a few lines, then its cards (to
 * search, filter and sort right here, its pinned ones and sections first) and its reviews, in tabs.
 * The public page (/vendedores/:handle) and the editor's "Vista previa" both render it; `preview`
 * shows it as a buyer sees it, with the buttons off and the links inert.
 */
export function ShopView({ seller: s, preview = false }: { seller: SellerPublic; preview?: boolean }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const couponOff = useCouponOff();
  const auth = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"cards" | "reviews">("cards");
  const [following, setFollowing] = useState<boolean | null>(null);
  const [shared, setShared] = useState(false);
  const [factsOpen, setFactsOpen] = useState(false);
  const off = preview ? { disabled: true, title: t("mkt.shop.previewOff") } : {};
  const accent = s.shop?.accent ?? null;
  const isFollowing = following ?? s.following;
  const minutes = s.stats.responseMinutes;
  const responseText = minutes === null ? null : minutes < 60 ? t("mkt.minutes", { count: Math.max(1, minutes) }) : minutes < 1440 ? t("mkt.hours", { count: Math.round(minutes / 60) }) : t("mkt.days", { count: Math.round(minutes / 1440) });
  // Only what there's something to say about yet: a new shop shows none of the three.
  const stats = (
    [
      [t("mkt.metricResponse"), responseText],
      [t("mkt.metricCancel"), s.stats.cancelPct === null ? null : `${s.stats.cancelPct}%`],
      [t("mkt.metricOnTime"), s.stats.onTimePct === null ? null : `${s.stats.onTimePct}%`],
    ] as const
  ).filter((x): x is readonly [string, string] => x[1] !== null);

  return (
    <div
      className={`${preview ? "seller-page shop-preview" : "container section seller-page"}${accent ? " has-accent" : ""}`}
      style={accent ? ({ "--shop-accent": SHOP_ACCENTS[accent] } as CSSProperties) : undefined}
    >
      {s.announcement && <AnnouncementBar announcement={s.announcement} preview={preview} />}
      {s.shop?.banner && <ShopBanner image={s.shop.banner} target={s.bannerTarget ?? null} />}
      <div className="shop-top">
      <header className="seller-head">
        {s.shop?.logo ? <img className="shop-logo" src={imageUrl(s.shop.logo)} alt="" width={88} height={88} /> : <Avatar url={s.avatarUrl} name={s.displayName} size={88} />}
        <div className="stack-sm">
          <h1>{s.displayName}</h1>
          {s.shop?.tagline && <p className="shop-tagline">{s.shop.tagline}</p>}
          <p className="muted">
            @{s.handle}
            {s.city ? ` · ${[s.city, s.province].filter(Boolean).join(", ")}` : ""} · {t("mkt.memberSince", { date: dateFmt(s.memberSince, locale) })}
            {s.avatarLevel ? ` · ${t("mkt.avatarLevel", { level: s.avatarLevel })}` : ""}
          </p>
          <Badges badges={s.badges} />
          {s.company && <p className="muted small">{t("mkt.companyLine", { name: s.company.legalName, cuit: formatCuit(s.company.cuit) })}</p>}
          <p className="seller-score">
            <Stars value={s.ratingAvg} count={s.ratingCount} /> · <strong>{t("mkt.salesCount", { count: s.sales })}</strong> · {t("mkt.followers", { count: s.followers })}
          </p>
          {stats.length > 0 && (
            <dl className="shop-stats" aria-label={t("mkt.reputation")} title={t("mkt.metricsNote")}>
              {stats.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}
          <div className="seller-actions">
            {auth.user && (
              <button
                type="button"
                className={isFollowing ? "btn btn-outline btn-sm" : "btn btn-primary btn-sm"}
                aria-pressed={isFollowing}
                {...off}
                onClick={async () => {
                  setFollowing(!isFollowing);
                  await marketApi.follow(s.handle, !isFollowing).catch(() => setFollowing(isFollowing));
                }}
              >
                {isFollowing ? t("mkt.following") : t("mkt.follow")}
              </button>
            )}
            {!auth.user && (
              <button type="button" className="btn btn-primary btn-sm" {...off} onClick={() => navigate(`/ingresar?next=${encodeURIComponent(`/vendedores/${s.handle}`)}`)}>
                {t("mkt.follow")}
              </button>
            )}
            <button
              type="button"
              className="btn btn-link btn-sm"
              {...off}
              onClick={async () => {
                const url = `${window.location.origin}/@${s.handle}`;
                if (navigator.share) await navigator.share({ title: s.displayName, url }).catch(() => {});
                else await navigator.clipboard?.writeText(url).then(() => setShared(true));
              }}
            >
              {shared ? t("mkt.copied") : t("mkt.share")}
            </button>
            {!preview && <ReportButton target="seller" targetId={s.handle} />}
          </div>
        </div>
      </header>

      {/* On a phone, folded under one line: the cards come sooner. Wide, always open (CSS). */}
      <button type="button" className="shop-facts-toggle" aria-expanded={factsOpen} aria-controls="shop-facts" onClick={() => setFactsOpen(!factsOpen)}>
        {t("mkt.howSells")} <span aria-hidden="true">{factsOpen ? "▴" : "▾"}</span>
      </button>
      <dl id="shop-facts" className={factsOpen ? "shop-facts is-open" : "shop-facts"} aria-label={t("mkt.howSells")}>
        <div>
          <dt>{t("mkt.payments")}</dt>
          <dd>
            <PaymentChips methods={s.payments} />
          </dd>
        </div>
        {deliveryModesOf(s.delivery).map((m) => (
          <div key={m}>
            <dt>{DELIVERY_LABEL[m][locale]}</dt>
            <dd>
              {m === "hand" && s.delivery.hand
                ? `${s.delivery.hand.zones.map((z) => ZONE_LABEL[z][locale]).join(", ")}${s.delivery.hand.areas ? ` (${s.delivery.hand.areas})` : ""} · ${s.delivery.hand.costArs ? ars(s.delivery.hand.costArs) : t("mkt.free")}`
                : m === "ship" && s.delivery.ship
                  ? `${s.delivery.ship.carriers || "—"} · ${s.delivery.ship.costArs === null ? t("mkt.quoted") : ars(s.delivery.ship.costArs)}`
                  : m === "pickup"
                    ? t("mkt.pickupAfterPay")
                    : t("mkt.arrangeChat")}
            </dd>
          </div>
        ))}
        {s.delivery.schedule && (
          <div>
            <dt>{t("mkt.schedule")}</dt>
            <dd>{scheduleText(s.delivery.schedule, locale)}</dd>
          </div>
        )}
        {s.minOrderArs && (
          <div>
            <dt>{t("mkt.minOrder")}</dt>
            <dd>{ars(s.minOrderArs)}</dd>
          </div>
        )}
        {s.coupon && (
          <div>
            <dt>{t("mkt.coupon")}</dt>
            <dd>
              <span className="coupon-chip">{s.coupon.code}</span> {couponOff(s.coupon)}
            </dd>
          </div>
        )}
        <div>
          <dt>{t("mkt.offers")}</dt>
          <dd>{s.acceptsOffers ? t("mkt.offersYes") : t("mkt.offersNo")}</dd>
        </div>
      </dl>
      </div>

      {s.vacationUntil && <p className="notice">{t("mkt.onVacation", { date: dateFmt(s.vacationUntil, locale, { day: "numeric", month: "long" }) })}</p>}
      {s.bio && <p className="pre-line seller-bio">{s.bio}</p>}
      {s.shop && (s.shop.about || s.shop.links.length > 0) && (
        <section className="shop-about" aria-labelledby="about-title">
          <h2 id="about-title" className="small-title">
            {t("mkt.shop.aboutTitle")}
          </h2>
          {/* Plain text: line breaks only, never HTML. */}
          {s.shop.about && <p className="pre-line">{s.shop.about}</p>}
          {s.shop.links.length > 0 && (
            <ul className="shop-links">
              {s.shop.links.map((l) => (
                <li key={l.url}>
                  <a href={l.url} target="_blank" rel="nofollow ugc noopener noreferrer">
                    {l.label} ↗
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div className="shop-tabs" role="tablist">
        <button type="button" role="tab" id="shop-tab-cards" aria-controls="shop-panel" aria-selected={tab === "cards"} onClick={() => setTab("cards")}>
          {t("mkt.sellerCards")}
        </button>
        <button type="button" role="tab" id="shop-tab-reviews" aria-controls="shop-panel" aria-selected={tab === "reviews"} onClick={() => setTab("reviews")}>
          {t("mkt.reviews", { count: s.ratingCount })}
        </button>
      </div>
      <div id="shop-panel" role="tabpanel" aria-labelledby={tab === "cards" ? "shop-tab-cards" : "shop-tab-reviews"}>
        {tab === "cards" ? (
          <AsBuyer.Provider value={preview}>
            <ShopCards seller={s} preview={preview} />
          </AsBuyer.Provider>
        ) : (
          <Reviews seller={s} />
        )}
      </div>
    </div>
  );
}

/** The shop's cards: searched, filtered and sorted right here; its pinned listings and sections first while nothing is. */
function ShopCards({ seller: s, preview }: { seller: SellerPublic; preview: boolean }) {
  const { t, locale, pick } = useI18n();
  const { categories } = useCatalog().catalog;
  const { game } = useGame();
  const [filters, setFilters] = useShopFilters(s.handle, preview);
  // The owner gets no cart buttons on their own cards; the editor's preview shows the shop as a buyer sees it (buttons inert).
  const buy = preview || !s.mine;
  const [open, setOpen] = useState(false);
  const [view, setView] = useListingView();
  const asked = { ...filters, game, finish: filters.finish && gameFinishes(game).includes(filters.finish) ? filters.finish : null };
  const key = shopQuery(asked);
  const listings = useAsync((signal) => marketApi.listings(asked, signal), [key]);
  const results = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  // A new search or page starts at the results, not at the banner; a page that's already there stays.
  useEffect(() => {
    if (first.current) return void (first.current = false);
    const el = results.current;
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [key]);
  const apply = (patch: Partial<MarketFilters>) => setFilters({ ...filters, page: 1, ...patch });
  const clear = () => setFilters(shopFilters(new URLSearchParams(), s.handle));
  const filtered = isFiltered(filters);
  const front = !filtered && filters.page === 1;
  const pins = front ? (s.pins ?? []) : [];
  const sections = front ? (s.sections ?? []) : [];
  const totalPages = listings.data ? Math.max(1, Math.ceil(listings.data.total / listings.data.pageSize)) : 1;
  const chips = [
    ...(filters.q ? [{ key: "q", label: `“${filters.q}”`, patch: { q: "" } }] : []),
    ...filters.categories.map((c) => ({ key: `c-${c}`, label: pick(categories.find((x) => x.slug === c)?.name ?? { es: c, en: c }), patch: { categories: filters.categories.filter((x) => x !== c) } })),
    ...filters.sets.map((v) => ({ key: `s-${v}`, label: v, patch: { sets: filters.sets.filter((x) => x !== v) } })),
    ...filters.rarities.map((r) => ({ key: `r-${r}`, label: rarityLabel(r, locale) ?? r, patch: { rarities: filters.rarities.filter((x) => x !== r) } })),
  ];

  return (
    <div ref={results} className="shop-cards">
      <div className="catalog-toolbar shop-toolbar">
        <form
          key={filters.q}
          role="search"
          className="shop-search"
          onSubmit={(e) => {
            e.preventDefault();
            apply({ q: String(new FormData(e.currentTarget).get("q") ?? "").trim() });
          }}
        >
          <input name="q" type="search" defaultValue={filters.q} aria-label={t("mkt.shop.search", { name: s.displayName })} placeholder={t("mkt.shop.search", { name: s.displayName })} />
          <button type="submit" className="btn btn-outline">
            {t("mkt.f.search")}
          </button>
        </form>
        <button type="button" className="btn btn-outline filters-toggle" aria-expanded={open} onClick={() => setOpen(true)}>
          {t("mkt.filters")}
        </button>
        <label className="sort-select">
          <span className="sr-only">{t("mkt.sort")}</span>
          <select value={filters.sort} onChange={(e) => apply({ sort: e.target.value as MarketFilters["sort"] })}>
            {SHOP_SORTS.map((x) => (
              <option key={x} value={x}>
                {t(`mkt.sort.${x}` as never)}
              </option>
            ))}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      <div className="catalog-layout">
        <aside className={open ? "catalog-sidebar is-open" : "catalog-sidebar"} aria-label={t("mkt.filters")}>
          <div className="sidebar-head">
            <h2>{t("mkt.filters")}</h2>
            <button type="button" className="icon-btn sidebar-close" aria-label={t("mkt.shop.closeFilters")} onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>
          {/* Re-mounted when the filters change (or the categories arrive) so the inputs show the applied ones. */}
          <MarketFiltersForm key={`${key}|${categories.length}`} shop filters={filters} onApply={(patch) => (apply(patch), setOpen(false))} onClear={() => (clear(), setOpen(false))} />
        </aside>

        <section className="catalog-results" aria-label={t("mkt.sellerCards")}>
          {chips.length > 0 && (
            <ul className="active-filters" aria-label={t("filters.active")}>
              {chips.map((chip) => (
                <li key={chip.key}>
                  <button type="button" className="filter-chip" aria-label={t("filters.remove", { label: chip.label })} onClick={() => apply(chip.patch)}>
                    {chip.label} <span aria-hidden="true">✕</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {pins.length > 0 && (
            <section aria-labelledby="shop-pins" className="shop-pins">
              <h2 id="shop-pins" className="section-title">
                {t("mkt.shop.pinsTitle")}
              </h2>
              <ListingGrid items={pins} showSeller={false} buy={buy} />
            </section>
          )}
          {sections.map((x, i) => {
            const patch = x.total > x.items.length ? sectionPatch(x) : null;
            const label = t("mkt.shop.sectionMore", { count: x.total });
            return (
              <section key={i} aria-labelledby={`shop-section-${i}`} className="shop-pins">
                <div className="section-head">
                  <h2 id={`shop-section-${i}`} className="section-title">
                    {x.title}
                  </h2>
                  {patch &&
                    (preview ? (
                      <button type="button" className="btn btn-link" onClick={() => apply(patch)}>
                        {label}
                      </button>
                    ) : (
                      <Link to={{ search: shopQuery({ ...filters, page: 1, ...patch }) }} className="btn btn-link">
                        {label}
                      </Link>
                    ))}
                </div>
                <ListingGrid items={x.items} showSeller={false} buy={buy} />
              </section>
            );
          })}

          {(pins.length > 0 || sections.length > 0) && <h2 className="section-title">{t("mkt.shop.allCards")}</h2>}
          {listings.data && (
            <p className="muted" aria-live="polite">
              {t("mkt.results", { count: listings.data.total })}
            </p>
          )}
          {listings.error ? (
            <ErrorMessage onRetry={listings.reload} />
          ) : !listings.data ? (
            <Loading />
          ) : listings.data.items.length === 0 ? (
            filtered ? (
              <div className="status">
                <p>{t("mkt.empty")}</p>
                <button type="button" className="btn btn-outline" onClick={clear}>
                  {t("catalog.clearFilters")}
                </button>
              </div>
            ) : (
              <p className="muted">{t("mkt.sellerNoCards")}</p>
            )
          ) : (
            <div className={listings.loading ? "is-refreshing" : undefined}>
              {view === "list" ? <ListingRows items={listings.data.items} buy={buy} /> : <ListingGrid items={listings.data.items} showSeller={false} buy={buy} />}
              <Pagination page={filters.page} totalPages={totalPages} onPage={(page) => setFilters({ ...filters, page })} />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Reviews({ seller: s }: { seller: SellerPublic }) {
  const { t, locale } = useI18n();
  const handle = s.handle;
  const max = Math.max(1, ...Object.values(s.ratingDistribution));
  const [pages, setPages] = useState(1);
  const [extra, setExtra] = useState<ReviewView[]>([]);
  const [more, setMore] = useState<boolean | null>(null);
  const first = useAsync((signal) => marketApi.sellerReviews(handle, 1, signal), [handle]);
  const items = [...(first.data?.items ?? []), ...extra];
  const hasMore = more ?? first.data?.hasMore ?? false;
  return (
    <section aria-label={t("mkt.reviews", { count: s.ratingCount })} className="reviews">
      {s.ratingCount > 0 && (
        <ul className="rating-bars">
          {([5, 4, 3, 2, 1] as const).map((n) => (
            <li key={n}>
              <span>{n}★</span>
              <span className="bar" aria-hidden="true">
                <span style={{ width: `${(s.ratingDistribution[n] / max) * 100}%` }} />
              </span>
              <span className="muted small">{s.ratingDistribution[n]}</span>
            </li>
          ))}
        </ul>
      )}
      {items.length === 0 ? (
        <p className="muted">{t("mkt.noReviewsYet")}</p>
      ) : (
        <ul className="review-list">
          {items.map((r) => (
            <li key={r.orderNumber} className="review">
              <div className="review-head">
                <span className="review-stars" aria-label={t("mkt.starsShort", { count: r.stars })}>
                  {"★".repeat(r.stars)}
                  <span className="stars-empty">{"★".repeat(5 - r.stars)}</span>
                </span>
                <strong>{r.author.name}</strong>
                <span className="muted small">{dateFmt(r.createdAt, locale, { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              {r.body && <p>{r.body}</p>}
              {r.reply && (
                <p className="review-reply">
                  <strong>{t("mkt.sellerReply")}</strong> {r.reply}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <button
          type="button"
          className="btn btn-outline"
          onClick={async () => {
            const next = await marketApi.sellerReviews(handle, pages + 1);
            setExtra((e) => [...e, ...next.items]);
            setMore(next.hasMore);
            setPages(pages + 1);
          }}
        >
          {t("mkt.moreReviews")}
        </button>
      )}
    </section>
  );
}

/** The shop's announcement: its line, and the coupon with a way to copy it. */
function AnnouncementBar({ announcement: a, preview }: { announcement: ShopAnnouncement; preview: boolean }) {
  const { t, locale } = useI18n();
  const couponOff = useCouponOff();
  const [copied, setCopied] = useState(false);
  const c = a.coupon;
  return (
    <aside className="shop-announcement" aria-label={t("mkt.shop.announcementLabel")}>
      <span>{a.text}</span>
      {c && (
        <span>
          <span className="coupon-chip">{c.code}</span> {couponOff(c)}
          {c.scope === "set" && c.setName ? ` ${t("mkt.shop.announcementSet", { set: c.setName })}` : c.scope === "products" ? ` ${t("mkt.shop.announcementProducts")}` : ""}{" "}
          <button type="button" className="btn btn-link btn-sm" disabled={preview} onClick={() => void navigator.clipboard?.writeText(c.code).then(() => setCopied(true))}>
            {copied ? t("mkt.copied") : t("mkt.shop.announcementCopy")}
          </button>
        </span>
      )}
      {a.until && <span className="small">{t("mkt.shop.announcementUntil", { date: dateFmt(`${a.until}T12:00:00`, locale, { day: "numeric", month: "long" }) })}</span>}
    </aside>
  );
}

/** The shop's banner, leading to one of its sections (on this page) or one of its listings when the shop chose one. */
function ShopBanner({ image, target }: { image: string; target: ShopBannerTarget | null }) {
  const { t } = useI18n();
  const img = <img className="shop-banner" src={imageUrl(image)} alt="" width={1600} height={400} />;
  if (!target) return img;
  const label = t("mkt.shop.bannerGo", { name: target.kind === "section" ? target.title : target.name });
  return target.kind === "section" ? (
    <a className="shop-banner-link" href={`#shop-section-${target.index}`} aria-label={label}>
      {img}
    </a>
  ) : (
    <Link className="shop-banner-link" to={`/mercado/${target.id}`} aria-label={label}>
      {img}
    </Link>
  );
}
